import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@folio/components';
import { QuoteTierCustomFieldRow } from '@folio/components/module-elements/QuoteTierCustomFieldRow';
import { MAX_FLEET_SIZE } from '@folio/constants';
import { getLatestAsset } from '@folio/utils/useCurrentActiveAsset';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { usePolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import { useQueries, useSuspenseQuery } from '@tanstack/react-query';
import {
  configControllerGetPublicProductConfigQueryOptions,
  keyValueFieldsToObject,
  viewSpecificControllerListPolicyAssetsQueryOptions,
  type ListPolicyAssetsOutput,
} from 'korint-sdk';
import { CheckIcon, PlusIcon } from 'lucide-react';
import { tierBadgeVariants } from '../QuoteTierModule/QuoteTableChosenTier';
import { useTranslation } from 'react-i18next';
import { Group } from '@folio/components/ui/flex';

function QuoteTierCompareChosenTier({ branches }: { branches: Array<string | undefined> }) {
  const { t } = useTranslation();

  const { policy } = usePolicyOnDashboard();

  const results = useQueries({
    queries: branches.map(branch => ({
      ...viewSpecificControllerListPolicyAssetsQueryOptions(policy.id, {
        branchId: branch,
        count: MAX_FLEET_SIZE,
      }),
      select: (data: ListPolicyAssetsOutput) => {
        return getLatestAsset(
          data.data.map(asset => ({
            ...asset,
            customFields: keyValueFieldsToObject(asset.customFields ?? []),
          })),
        );
      },
    })),
  });

  return (
    <tr>
      <td className="font-semibold text-sm">{t('quoteTierCompareModule.chosenTier')}</td>
      {results.map((asset, index) => {
        const chosenTier = asset?.data?.quote?.options.tier;

        if (asset.status !== 'success') {
          return <th className="p-1" key={`loading-${branches[index]}`} />;
        }

        return (
          <th className="uppercase p-1" key={`${asset.data.id}-${branches[index]}`}>
            {asset.data.quote && (
              <div
                className={tierBadgeVariants({
                  tier: chosenTier as never,
                })}
              >
                {t([`${policy?.productId}.tiers.${chosenTier}.label`, `tiers.${chosenTier}.label`])}
              </div>
            )}
          </th>
        );
      })}
    </tr>
  );
}

function QuoteTierCompareIncludedPerils({
  branches,
  required,
}: {
  branches: Array<string | undefined>;
  required?: boolean;
}) {
  const { t } = useTranslation();

  const { policy } = usePolicyOnDashboard();
  const { data: config } = useSuspenseQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy.productId),
  });

  const results = useQueries({
    queries: branches.map(branch => ({
      ...viewSpecificControllerListPolicyAssetsQueryOptions(policy.id, {
        branchId: branch,
        count: MAX_FLEET_SIZE,
      }),
      select: (data: ListPolicyAssetsOutput) => {
        return getLatestAsset(
          data.data.map(asset => ({
            ...asset,
            customFields: keyValueFieldsToObject(asset.customFields ?? []),
          })),
        );
      },
    })),
  });

  const uniqueTiers = [...new Set(results.map(result => result?.data?.quote?.options.tier))];

  const availablePerilsConfig = config?.quote?.availableConfigs
    .filter(item => uniqueTiers.includes(item.tier))
    .flatMap(item => item.perils?.filter(peril => Boolean(peril.required) === required))
    .filter(
      (peril, index, self) =>
        self.findIndex(t => t?.id && peril?.id && t.id === peril.id) === index,
    );

  return availablePerilsConfig?.map((peril, index) => {
    const perilInTiers = results.map(result =>
      result?.data?.quote?.options?.perils?.find(assetPeril => peril!.id === assetPeril.id),
    );

    const isPerilIncludedForAllAssets = perilInTiers.every(
      peril => peril?.id === perilInTiers[0]?.id,
    );

    const isPerilNotIncluded = perilInTiers.every(peril => peril === undefined);

    if (!peril || isPerilNotIncluded) return null;

    return (
      <tr
        key={peril.id}
        className={`text-sm hover:bg-slate-50 ${!isPerilIncludedForAllAssets ? 'bg-slate-100' : ''}`}
      >
        <td className="pr-12 font-semibold bg-white">
          {!index &&
            (required
              ? t('quoteTierCompareModule.includedPerils')
              : t('quoteTierCompareModule.optionalPerils'))}
        </td>
        {branches.map((branch, branchIndex) => {
          const asset = results[branchIndex]?.data;

          const includedPerilsInTiers = asset?.quote?.options?.perils?.find(
            assetPeril => peril!.id === assetPeril.id,
          );

          if (!includedPerilsInTiers) {
            return <td className="p-1" key={branch ?? 'BASE'} />;
          }

          const configPeril = availablePerilsConfig?.find(p => p?.id === peril.id);
          const rawValue = includedPerilsInTiers.option ?? configPeril?.options?.[0];
          const value = rawValue
            ? t([
                `${policy?.productId}.perils.${peril.id}.options.${rawValue}`,
                `options.${rawValue}`,
                rawValue,
              ])
            : undefined;

          return (
            <td className="p-1" key={branch ?? 'BASE'}>
              <Group gap={0}>
                {required ? (
                  <CheckIcon className="h-4 w-4 mr-3 shrink-0" />
                ) : (
                  <PlusIcon className="h-4 w-4 mr-3 shrink-0" />
                )}
                {t(`${policy?.productId}.perils.${peril.id}.label`, {
                  value,
                  count: Number(rawValue) || 0,
                })}
                {includedPerilsInTiers.option &&
                  ` (${t([
                    `${policy?.productId}.perils.${peril.id}.options.${includedPerilsInTiers.option}`,
                    `options.${includedPerilsInTiers.option}`,
                  ])})`}
              </Group>
            </td>
          );
        })}
      </tr>
    );
  });
}

type QuoteTierCompareModuleProps = {
  /**
   * The branches to compare.
   */
  branches: Array<string | undefined>;
};

export function QuoteTierCompareModule({ branches }: QuoteTierCompareModuleProps) {
  const { t } = useTranslation();
  const quoteTierModuleConfig = usePolicyProductConfig('quoteTierModuleConfig');

  if (quoteTierModuleConfig.isCompareDisabled) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('quoteTierCompareModule.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table className="table-fixed">
          <TableHeader className="[&_tr]:border-b-0">
            <TableRow>
              <TableHead />
              {branches.map((branch, index) => (
                <TableHead key={branch || index} className="">
                  {index === 0 && branches.length > 1
                    ? t('compareTable.previousInformation')
                    : t('compareTable.newInformation')}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {quoteTierModuleConfig.isChosenTierDisplayed && (
              <QuoteTierCompareChosenTier branches={branches} />
            )}

            <QuoteTierCompareIncludedPerils branches={branches} required={true} />
            <QuoteTierCompareIncludedPerils branches={branches} required={false} />
            {quoteTierModuleConfig.fieldsConfig.map(fieldConfig => (
              <QuoteTierCustomFieldRow
                branches={branches}
                key={fieldConfig.label}
                label={fieldConfig.label}
                fieldPath={fieldConfig.path}
                type={fieldConfig.type}
              />
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
